import { useTenant, ImpersonatedRole } from "@/contexts/TenantContext";
import { useAuth } from "@/core/auth/AuthProvider";
import { Shield, Building2, Briefcase, Eye, X } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/shared/components/ui/dropdown-menu";

const ROLE_OPTIONS: { value: ImpersonatedRole; label: string; icon: React.ReactNode; description: string }[] = [
  {
    value: 'admin',
    label: 'Admin View',
    icon: <Shield className="h-4 w-4 text-amber-500" />,
    description: 'Full platform access, all data visible',
  },
  {
    value: 'agency_owner',
    label: 'Agency Owner View',
    icon: <Building2 className="h-4 w-4 text-blue-500" />,
    description: 'Agency-scoped data only',
  },
  {
    value: 'company_owner',
    label: 'Client Owner View',
    icon: <Briefcase className="h-4 w-4 text-green-500" />,
    description: 'Client-scoped data only',
  },
];

export function RoleImpersonator() {
  const { hasRole } = useAuth();
  const { impersonatedRole, setImpersonatedRole, effectiveRole } = useTenant();

  if (!hasRole("admin")) {
    return null;
  }

  const currentRoleOption = ROLE_OPTIONS.find(r => r.value === effectiveRole);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        <Eye className="h-4 w-4" />
        <span>View As</span>
        {impersonatedRole && (
          <Badge variant="outline" className="ml-auto text-xs">
            {impersonatedRole === 'admin' ? 'Admin' : 
             impersonatedRole === 'agency_owner' ? 'Agency' : 'Client'}
          </Badge>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-56">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Impersonate Role
        </div>
        {ROLE_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => setImpersonatedRole(option.value)}
            className={effectiveRole === option.value ? 'bg-accent' : ''}
          >
            <div className="flex items-start gap-2 w-full">
              {option.icon}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{option.label}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {option.description}
                </div>
              </div>
              {effectiveRole === option.value && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Active
                </Badge>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        {impersonatedRole && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setImpersonatedRole(null)}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4 mr-2" />
              Clear Impersonation
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
